// UC20-12 / UC20-15 / UC30-07 — the catalog change history: every kind for the
// System Administrator, the sites of its own Registrar for a Registrar
// Administrator (docs/shape-uc-30-sites.md §10).
import { apiClient } from '../../app/api-client';

export type HistoryEntityKind = 'GEOGRAPHY' | 'MEDICINE' | 'MEDICINE_CODE' | 'SITE';

export const AUDIT_SOURCES = [
  'USER_UI',
  'RSUD_SYNC',
  'NOMENCLATOR_SYNC',
  'MCONNECT_EVENT',
  'CUATM_IMPORT',
  // The UC42 System API — the Registrar's own system writing its locations.
  'REGISTRATOR_API',
  // The UC20-09 / UC20-10 sweep that retires medicine codes for age.
  'STALENESS_SWEEP',
  // The UC20-06 file import, by the administrator who uploaded it.
  'MANUAL_IMPORT',
  'SYSTEM',
] as const;

export type AuditSource = (typeof AUDIT_SOURCES)[number];

// WHY a row was written, when the writer recorded one. The server drops any
// label it does not know (`history-diff.ts`), and the interface renders only the
// ones it can translate: an untranslated reason would put machine text on the
// screen, which is the same defect as an untranslated server refusal (SC-006).
export const HISTORY_REASONS = [
  'MEDICINE_DEACTIVATED',
  'MEDICINE_REACTIVATED',
  'CODE_DEACTIVATED',
  'CODE_ACTIVATED',
  'STALE_NOT_IN_FEED',
  'STALE_AUTHORIZATION_EXPIRED',
  'GEOGRAPHY_NOT_IN_CUATM',
  'MEDICINE_KEY_MERGED',
] as const;

export type HistoryReason = (typeof HISTORY_REASONS)[number];

export interface HistoryFieldChange {
  field: string;
  before: string;
  after: string;
  redacted: boolean;
}

// A geography reference the log can no longer name — the position is gone from
// the nomenclator and from its own journal. Mirrors REFERENCE_MISSING in
// `apps/catalog-service/src/history/history-diff.ts`, which sends this token in
// place of the raw uuid `docs/ui-shape.md` forbids any screen to print.
export const REFERENCE_MISSING = 'DELETED';

// One more fact that identifies the row, beside `entityLabel`: the fields that
// complete a medicine's identity beside its DCI (caiet §2: formă, doză, volum,
// divizare), the type and CUATM code of a geographic position.
export interface HistoryEntityAttribute {
  field: string;
  value: string;
}

export interface HistoryEntry {
  id: string;
  entityKind: HistoryEntityKind;
  entityId: string;
  entityLabel: string;
  // Absent on a response from a server that predates the identity line.
  entityAttributes?: HistoryEntityAttribute[];
  changeType: string;
  actorId: string;
  actorRole: string;
  // No actor name (CF-63, SEC-14): the catalogue trail keeps the masked IDNP and the role only.
  source: string;
  // Empty, or absent altogether on a row written before the reason existed.
  reason?: string;
  recordedAtIso: string;
  changes: HistoryFieldChange[];
}

export interface HistoryPage {
  entries: HistoryEntry[];
  // Every row matching the same filters as the page, not only the ones on it.
  // The server reads it in the same statement as the page, so the footer's
  // `din {total}` cannot contradict its own `{from}–{to}`.
  total: number;
}

export interface HistoryQuery {
  kinds: HistoryEntityKind[];
  entityId?: string;
  from?: string;
  to?: string;
  source?: string;
  q?: string;
  limit?: number;
  // Rows to skip, `(page - 1) * limit`.
  offset?: number;
}

export async function fetchHistory(query: HistoryQuery): Promise<HistoryPage> {
  const params = new URLSearchParams({ kinds: query.kinds.join(',') });
  if (query.entityId) params.set('entityId', query.entityId);
  // The date pickers speak calendar days; the range has to cover the whole
  // closing day or an edit made this afternoon falls outside "up to today".
  if (query.from) params.set('from', `${query.from}T00:00:00.000Z`);
  if (query.to) params.set('to', `${query.to}T23:59:59.999Z`);
  if (query.source) params.set('sources', query.source);
  if (query.q) params.set('q', query.q);
  if (query.limit) params.set('limit', String(query.limit));
  if (query.offset) params.set('offset', String(query.offset));

  const { data } = await apiClient.get<HistoryPage>(`/admin/history?${params.toString()}`);
  return { entries: data.entries ?? [], total: data.total ?? 0 };
}
